import { FormikProvider, useFormik } from "formik";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";

import { formatNumber } from "@audacia-hq/shared/utils";

import { useMember } from "../../context/MemberContext";
import Button from "../Button";
import PhoneNumberValidationModal from "../members/PhoneNumberValidationModal";

const PhoneInput = dynamic(() => import("../PhoneInput"));
interface Props {
  defaultView: "ADD" | "SELECT";
  showToggle?: boolean;
  onSelectPhone: (phoneUid: string) => void;
  onAddPhone: (phoneUid: string) => void;
  onCancel?: () => void;
  preselect?: string;
  variant?: "welcomeOffer" | undefined;
}

const PhoneNumberManagement: React.FC<Props> = ({
  defaultView, showToggle = true, onSelectPhone, onAddPhone, onCancel, preselect, variant,
}) => {
  const { t } = useTranslation(["member", "welcomeOffer"]);
  const { member, handleMemberGet } = useMember();
  const [view, setView] = useState(defaultView);
  const [loading, setLoading] = useState<boolean>(false);
  const [phoneUid, setPhoneUid] = useState<string>(preselect || member?.phoneNumbers[0]?.phoneUid);
  const [phoneToValidate, setPhoneToValidate] = useState<string>();

  useEffect(() => {
    if (view === "SELECT") onSelectPhone(phoneUid);
    if (view === "ADD") onSelectPhone(undefined);
  }, [phoneUid, view]);

  const formik = useFormik<{ phoneNumber: string }>({
    initialValues: { phoneNumber: "" },
    onSubmit: (async (form) => {
      if (form.phoneNumber.length === 0) return;
      setLoading(true);
      setPhoneToValidate(form.phoneNumber);
    }),
  });

  const handleClose = (error?: string) => {
    setPhoneToValidate(undefined);
    setLoading(false);
    if (error) {
      toast.error(t(`common:errors.${error}`), { autoClose: 5000 });
    } else if (onCancel) onCancel();
  };

  const handleValidated = (phoneUid: string) => {
    setPhoneToValidate(undefined);
    setLoading(false);
    onAddPhone(phoneUid);
    setPhoneUid(phoneUid);
    formik.setFieldValue("phoneNumber", "");
    if (showToggle) {
      setView("SELECT");
    }
    handleMemberGet();
  };

  return (
    <div className="w-full">
      {member?.phoneNumbers?.length && view === "SELECT"
        ? (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor="phoneSelect" className="text-left block">
                {t("phoneNumber.callUsingExistingPhone")}
              </label>
              <select
                id="phoneSelect"
                data-pw="phoneSelect"
                value={phoneUid}
                onChange={(e) => setPhoneUid(e.target.value)}
                className="input text-sm"
              >
                {member.phoneNumbers.map((p) => (
                  <option value={p.phoneUid} key={p.phoneUid}>
                    {formatNumber(p.phoneNumber, "INTERNATIONAL", p.country)}
                  </option>
                ))}
              </select>
              {showToggle && (
                <div className="text-center">
                  <button
                    className="cursor-pointer text-blue-700 text-sm mt-5"
                    onClick={() => setView("ADD")}
                    type="button"
                  >
                    {t("phoneNumber.useNewPhone")}
                  </button>
                </div>
              )}
            </div>
          </>
        )
        : (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor="phoneSelect">
                {t("phoneNumber.callUsingNewPhone")}
              </label>
              <FormikProvider value={formik}>
                <div className="">
                  <PhoneInput
                    className="input"
                    id="phoneInput"
                    name="phoneNumber"
                    dataPw="phoneNumberInput"
                    selectedCountry={member?.country}
                  />
                </div>
              </FormikProvider>
            </div>
            {variant === "welcomeOffer" ? (
              <div className="flex flex-col md:flex-row-reverse mt-12 gap-3">
                <div>
                  <Button
                    className="w-full"
                    size="medium+"
                    render="primary"
                    data-pw="phoneNumberSaveButton"
                    onClick={() => formik.submitForm()}
                  >
                    {t("common:add")}
                  </Button>
                </div>
                <div><Button className="w-full" size="medium+" render="transparent" onClick={() => onCancel()}>{t("welcomeOffer:activateLater")}</Button></div>
              </div>
            ) : (
              <div className="w-full m-auto text-center mt-6">
                <Button
                  size="large"
                  onClick={() => formik.submitForm()}
                  loading={loading}
                  data-pw="phoneNumberSaveButton"
                >
                  {t("common:add")}
                </Button>
              </div>
            )}

            {!!member?.phoneNumbers?.length && showToggle && (
              <button
                className="w-full text-center cursor-pointer text-blue-700 text-sm"
                onClick={() => setView("SELECT")}
                type="button"
                data-pw="phoneNumberUseButton"
              >
                {t("phoneNumber.useExistingPhone")}
              </button>
            )}
            {phoneToValidate && (
              <PhoneNumberValidationModal
                phoneNumber={phoneToValidate}
                onClose={handleClose}
                onValidated={handleValidated}
                variant={variant}
              />
            )}
          </>
        )}
    </div>
  );
};

export default PhoneNumberManagement;
